// packages/shared/src/schemas/coupons.ts
import { z } from 'zod';

// Enums matching Prisma schema
export const CouponTypeEnum = z.enum(['PERCENTAGE', 'FIXED_AMOUNT', 'FREE_HOURS']);
export type CouponTypeValue = z.infer<typeof CouponTypeEnum>;

export const CouponRecurrenceEnum = z.enum([
  'NONE',
  'DAILY',
  'WEEKDAYS',
  'WEEKENDS',
  'CUSTOM_DAYS',
]);
export type CouponRecurrenceValue = z.infer<typeof CouponRecurrenceEnum>;

// Create Coupon Schema
export const CreateCouponSchema = z.object({
  code: z
    .string()
    .min(3, 'Code must be at least 3 characters')
    .max(20)
    .regex(/^[A-Z0-9_-]+$/, 'Code must be uppercase alphanumeric with underscores or hyphens'),
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().max(500).optional(),
  type: CouponTypeEnum,
  discountValue: z.number().positive('Discount value must be positive'),
  maxDiscount: z.number().positive().optional(),
  minBookingAmount: z.number().min(0).optional(),
  // Location targeting
  stateIds: z.array(z.string().uuid()).default([]),
  cityIds: z.array(z.string().uuid()).default([]),
  venueIds: z.array(z.string().uuid()).default([]),
  venueTypes: z.array(z.string()).default([]), // Venue type codes from VenueTypeConfig
  // Time restrictions
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format')
    .optional(),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format')
    .optional(),
  // Recurrence
  recurrence: CouponRecurrenceEnum.default('NONE'),
  customDays: z.array(z.number().min(0).max(6)).default([]),
  // Limits
  maxQuota: z.number().int().positive().optional(),
  perUserLimit: z.number().int().positive().default(1),
  isFirstBookingOnly: z.boolean().default(false),
});
export type CreateCouponRequest = z.infer<typeof CreateCouponSchema>;

// Update Coupon Schema
export const UpdateCouponSchema = CreateCouponSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateCouponRequest = z.infer<typeof UpdateCouponSchema>;

// Filter Schema for listing coupons
export const CouponFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  search: z.string().optional(),
  type: CouponTypeEnum.optional(),
  isActive: z.coerce.boolean().optional(),
  vendorId: z.string().uuid().optional(),
  venueId: z.string().uuid().optional(),
});
export type CouponFilterQuery = z.infer<typeof CouponFilterSchema>;

// Validate Coupon Schema (for booking flow)
export const ValidateCouponSchema = z.object({
  code: z.string().min(1, 'Coupon code is required'),
  venueId: z.string().uuid(),
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  subtotal: z.number().positive(),
});
export type ValidateCouponRequest = z.infer<typeof ValidateCouponSchema>;

// Available Coupons Query Schema
export const AvailableCouponsQuerySchema = z.object({
  venueId: z.string().uuid(),
  bookingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  subtotal: z.number().positive().optional(),
});
export type AvailableCouponsQuery = z.infer<typeof AvailableCouponsQuerySchema>;

// Response schemas
export const CouponResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  type: CouponTypeEnum,
  discountValue: z.number(),
  maxDiscount: z.number().nullish(),
  minBookingAmount: z.number().nullish(),
  vendorId: z.string().nullish(),
  stateIds: z.array(z.string()),
  cityIds: z.array(z.string()),
  venueIds: z.array(z.string()),
  venueTypes: z.array(z.string()),
  validFrom: z.string(),
  validUntil: z.string(),
  startTime: z.string().nullish(),
  endTime: z.string().nullish(),
  recurrence: CouponRecurrenceEnum,
  customDays: z.array(z.number()),
  maxQuota: z.number().nullish(),
  usageCount: z.number(),
  perUserLimit: z.number(),
  isFirstBookingOnly: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CouponResponse = z.infer<typeof CouponResponseSchema>;

export const CouponValidationResultSchema = z.object({
  isValid: z.boolean(),
  message: z.string(),
  discountAmount: z.number().optional(),
  coupon: CouponResponseSchema.optional(),
});
export type CouponValidationResult = z.infer<typeof CouponValidationResultSchema>;

// Coupon eligibility status for smart display
export const CouponEligibilityStatusEnum = z.enum(['eligible', 'locked', 'nearly_unlocked']);
export type CouponEligibilityStatus = z.infer<typeof CouponEligibilityStatusEnum>;

export const CouponPreviewSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  discountText: z.string(),
  estimatedSavings: z.number().optional(),
  status: CouponEligibilityStatusEnum,
  minBookingAmount: z.number().nullish(),
  maxDiscount: z.number().nullish(),
  amountNeededToUnlock: z.number().nullish(),
  percentToUnlock: z.number().nullish(),
  priority: z.number(),
  validFrom: z.string().nullish(),
  validUntil: z.string().nullish(),
  expiresAt: z.string().nullish(),
});
export type CouponPreview = z.infer<typeof CouponPreviewSchema>;

export const AvailableCouponsResultSchema = z.object({
  availableCoupons: z.array(CouponPreviewSchema),
  lockedCoupons: z.array(CouponPreviewSchema),
  nearlyUnlockedCoupons: z.array(CouponPreviewSchema),
  bestCoupon: CouponPreviewSchema.nullable(),
  maxPotentialSavings: z.number(),
  totalCouponCount: z.number(),
});
export type AvailableCouponsResult = z.infer<typeof AvailableCouponsResultSchema>;

// Paginated Coupons Response (unwrapped format after apiClient interceptor)
export const PaginatedCouponsResponseSchema = z.object({
  data: z.array(CouponResponseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type PaginatedCouponsResponse = z.infer<typeof PaginatedCouponsResponseSchema>;
